import React, { useCallback } from "react";
import { setExploreSelectedIds, updateSelectedExploreView } from 'store/actions';
import { connect } from "react-redux";
import { compose } from "recompose";
import PieChart, { Series as PieSeries, Label, Connector, Size as PieSize, Legend } from 'devextreme-react/pie-chart';
import { Chart, Series as ChartSeries, Size as ChartSize, Tooltip as ChartTooltip, CommonSeriesSettings } from 'devextreme-react/chart';
import { LoadIndicator } from 'devextreme-react/load-indicator';
import "./resultsCharts.css";
import { formatValue, truncateString } from 'utilities/format';
import { Grid, Select, MenuItem, InputBase } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { propertyGroupLevels } from "api/rex/spatial";

const styles = theme => ({
  root: {
    flexGrow: 1,
    justifyContent: 'center',
    overflowY: 'auto'
  }
});

const chartPaddingBottom = 10;

const ResultsCharts = ({ classes, objectId, propertyData, isMainIndicatorVisible, isAggregateData, groupLevel, propertyDataDisplay, chartsBucketsData,
  chartsBucketsIsLoading, updateSelectedExploreView, setExploreSelectedIds, resizableHeight, containerWidth }) => {
  const onDistributionTypeChange = (e) => {
    updateSelectedExploreView({ propertyDataDisplay: { ...propertyDataDisplay, chartsSelectedDistributionType: e.target.value }});
  }

  const getClusterNameCaption = useCallback(() => {
    if (isAggregateData) {
      return propertyGroupLevels.find(level => level.value === groupLevel)?.caption ?? groupLevel ?? '';
    }
    return "Address";
  }, [groupLevel, isAggregateData]);

  if (!propertyDataDisplay) return null;
  return (
    <Grid container className={classes.root} spacing={0}>
      <Grid item style={{paddingBottom: chartPaddingBottom}}>
        <PieChart
          id={'pie'}
          dataSource={propertyData} // TODO: check for issues from change from 'pins' to 'propertyData'
          palette={'Soft Pastel'}
          title={getClusterNameCaption() + ' Breakout'}
          type="doughnut"
          resolveLabelOverlapping='shift'
          animation={false}
          onPointClick={(e) => {
            if (e && e.target && e.target.data) {
              setExploreSelectedIds({ ids: [{ objectId, clusterID: e.target.data.clusterID }], from: 'Grid' });
            }
          }}
        >
          <PieSeries
            argumentField={'clusterName'}
            valueField={'propertiesCount'}
            smallValuesGrouping={{
              mode: 'topN',
              topCount: 5,
              groupName: 'Other'
            }}
          >
            <Label visible={true} format="fixedPoint" customizeText={(point) => {
              return truncateString(point.argumentText, 20, true) + "<br/>" +
                formatValue(point.value, {
                  type: 'fixed',
                  decimals: 0,
                  useSeparator: true,
                  abbreviation: true
                }, true) + " (" + point.percentText + ")";
            }}>
              <Connector visible={true} width={1} />
            </Label>
          </PieSeries>
          <Legend visible={false} />
          <PieSize height={resizableHeight - chartPaddingBottom - 48} /> {/* Subtract height of display type/source select */}
        </PieChart>
      </Grid>
      <Grid item style={{ position: 'relative', paddingBottom: chartPaddingBottom }}>
        <Select
          style={{ marginTop: 10, fontSize: 20 }}
          value={propertyDataDisplay.chartsSelectedDistributionType}
          onChange={onDistributionTypeChange}
          input={<InputBase className={classes.chartDistributionType} />}
        >
          <MenuItem value={"YearBuilt"}>Year Built Distribution</MenuItem>
          <MenuItem value={"AVMValue"}>AVM Distribution</MenuItem>
          <MenuItem value={"ForRentPrice"}>Asking Rent Distribution</MenuItem>
          <MenuItem value={"ModelRent"}>Model Rent Distribution</MenuItem>
          <MenuItem value={"CapRate"}>Cap Rate Distribution</MenuItem>
          <MenuItem value={"HPA1Yr"}>HPA 12mo Distribution</MenuItem>
        </Select>
        <Chart
          id={'chart'}
          dataSource={chartsBucketsData}
          style={{ marginTop: 10 }}
          legend={{ visible: false }}
        >
          <CommonSeriesSettings
            ignoreEmptyPoints={true}
          />
          <ChartSeries
            valueField={'propertiesCount'}
            argumentField={'bucketName'}
            name={'Vintage'}
            type={'bar'}
            ignoreEmptyPoints={true}
          //color={'#ffaa66'}
          />
          <ChartSize
            height={resizableHeight - 56 - chartPaddingBottom - 48} // Subtract height of Select, chart marginTop, chartPaddingBottom, and display type/source select
            width={containerWidth / 2}
          />
          <ChartTooltip
            enabled={true}
            location={'edge'}
            customizeTooltip={(arg) => {
              return {
                text: `${arg.argumentText}: ${formatValue(arg.value, {
                  type: 'fixed',
                  decimals: 0,
                  useSeparator: true,
                  abbreviation: true
                }, true)}`
              };
            }}
          />
        </Chart>
        {
          (isMainIndicatorVisible || chartsBucketsIsLoading) &&
          <LoadIndicator id={'medium-indicator'} height={40} width={40} style={{ position: 'absolute', left: `50%`, top: `50%` }} />
        }
      </Grid>
    </Grid>
  );
}

function mapState({ exploreViews, exploreData, exploreLoading }) {
  const selectedId = exploreViews.selectedView?.propertyDataDisplay?.selectedSourceId;
  const pdo = selectedId != null ? exploreData.propertyDataObjects[selectedId] : null;
  return {
    objectId: selectedId,
    isAggregateData: pdo?.isAggregateData,
    groupLevel: pdo?.groupLevel,
    chartsBucketsData: exploreData.chartsBucketsData,
    chartsBucketsIsLoading: exploreLoading.chartsBucketsIsLoading,
    propertyDataDisplay: exploreViews.selectedView?.propertyDataDisplay,
    isMainIndicatorVisible: pdo?.isMainIndicatorVisible,
    propertyData: pdo?.propertyData
  };
}

const mapDispatch = {
  setExploreSelectedIds,
  updateSelectedExploreView
}

export default compose(
  withStyles(styles), connect(mapState, mapDispatch)
)(ResultsCharts);
